import posthog, { type CaptureResult } from "posthog-js"

const EVENT_PREFIX = "rm_"
const PRODUCT = "reporting_manager"
const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com"

let started = false

function logAnalytics(message: string): void {
  if (import.meta.env.DEV) {
    console.info(`[PostHog] ${message}`)
  }
}

function getPostHogKey(): string | undefined {
  const key = import.meta.env.VITE_POSTHOG_KEY?.trim()
  return key || undefined
}

function isAnalyticsAllowed(): boolean {
  return import.meta.env.VITE_POSTHOG_ENABLED === "true"
}

export function initAnalytics(): void {
  const key = getPostHogKey()
  if (started || !isAnalyticsAllowed() || !key) {
    return
  }

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || DEFAULT_POSTHOG_HOST,
    capture_pageview: false,
    autocapture: {
      dom_event_allowlist: ["click"],
      element_allowlist: ["button", "a"],
    },
    person_profiles: "identified_only",
    disable_surveys: true,
    // Stamped here rather than via register() so reset() cannot clear it.
    before_send: (event: CaptureResult | null): CaptureResult | null => {
      if (event) {
        event.properties = { ...event.properties, product: PRODUCT }
      }
      return event
    },
  })
  started = true
  logAnalytics("initialized")
}

export function track(event: string, properties?: Record<string, unknown>): void {
  if (started) {
    posthog.capture(`${EVENT_PREFIX}${event}`, properties)
    logAnalytics(`captured ${EVENT_PREFIX}${event}`)
  }
}

export function trackPageview(path: string): void {
  if (started) {
    posthog.capture("$pageview", {
      path,
      $current_url: globalThis.location.href,
    })
    logAnalytics(`captured pageview for ${path}`)
  }
}

export function identifyUser(userId: string, tenantId?: string): void {
  if (!started) {
    return
  }

  posthog.identify(userId)
  if (tenantId) {
    posthog.group("tenant", tenantId)
  }
  logAnalytics(`identified user${tenantId ? " with tenant group" : ""}`)
}

export function resetAnalytics(): void {
  if (started) {
    posthog.reset()
    logAnalytics("reset identity")
  }
}

export function isAnalyticsEnabled(): boolean {
  return started
}